import { useQuery } from '@tanstack/react-query';
import { api } from './api';

/**
 * orgTimezone — the org's IANA timezone + the local date/time formatters the
 * calendar and reminder surfaces render through (calendar-tz-grid).
 *
 * The backend exposes organizations.timezone on GET /api/current-user; every
 * absolute timestamp (remind_at) and every wall-clock (event_time) is rendered
 * in THAT zone via Intl.DateTimeFormat, so a user in a different browser locale
 * still sees the org's calendar as the org keeps it. event_date arrives as a
 * clean 'YYYY-MM-DD' string (SQL-cast server-side) and is formatted locally
 * without ever constructing a UTC-midnight Date (which would shift the day).
 */

export const DEFAULT_ORG_TZ = 'America/Chicago';

interface CurrentUserTz {
  timezone?: string | null;
}

/**
 * The org's timezone, read from the cached /api/current-user envelope (shared
 * with the shell + OrgSettings queries — react-query dedupes the fetch). Falls
 * back to the platform default, then the browser zone, so rendering never breaks.
 */
export function useOrgTimezone(): string {
  const { data } = useQuery<CurrentUserTz>({
    queryKey: ['current-user'],
    queryFn: ({ signal }) => api.get<CurrentUserTz>('/api/current-user', signal),
    staleTime: 5 * 60 * 1000,
  });
  const tz = data?.timezone || browserTz() || DEFAULT_ORG_TZ;
  // Keep the module cache the pure formatDate/formatDateTime read in sync with
  // the resolved org zone. Idempotent assignment; safe during render.
  setActiveOrgTz(tz);
  return tz;
}

/** The browser's IANA zone, or '' when unavailable. */
function browserTz(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

/**
 * The module-level "active" org timezone. Kept in sync by useOrgTimezone (and an
 * effect in the app shell) so the pure `formatDate`/`formatDateTime` formatters —
 * which are called from column-cell renderers, table configs, and other non-hook
 * contexts that cannot read React context — still render in the org zone.
 * Empty until the first current-user resolves; formatters fall back to the
 * browser zone in the meantime (graceful, never a raw ISO).
 */
let activeOrgTz = '';

/** Publish the resolved org zone to the module cache the pure formatters read. */
export function setActiveOrgTz(tz: string | null | undefined): void {
  if (tz && isValidTimeZone(tz)) activeOrgTz = tz;
}

/** Resolve the zone a formatter should use: explicit → active org → browser → default. */
function resolveFormatTz(explicit?: string): string {
  if (explicit && isValidTimeZone(explicit)) return explicit;
  if (activeOrgTz && isValidTimeZone(activeOrgTz)) return activeOrgTz;
  const b = browserTz();
  return b && isValidTimeZone(b) ? b : DEFAULT_ORG_TZ;
}

/** Is `tz` a zone this runtime accepts? Bad zones throw a RangeError from Intl. */
export function isValidTimeZone(tz: string): boolean {
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * Format a bare event_date ('YYYY-MM-DD') as a clean local date, WITHOUT parsing
 * it through a Date at UTC midnight (which would render the previous day west of
 * UTC). Parts are split and passed to a local Date constructor (local midnight).
 * @example formatEventDate('2026-07-23') → 'Jul 23, 2026'
 */
export function formatEventDate(dateStr: string, opts?: Intl.DateTimeFormatOptions): string {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return dateStr;
  const dt = new Date(y, m - 1, d);
  return new Intl.DateTimeFormat('en-US', opts ?? { month: 'short', day: 'numeric', year: 'numeric' }).format(dt);
}

/**
 * Format event_time — a bare 'HH:MM'[:SS] wall clock that IS already org-local —
 * as a friendly 12-hour time. No zone conversion (there is no date/instant to
 * convert); this only prettifies the digits the org entered.
 * @example formatEventTime('08:00') → '8:00 AM'
 */
export function formatEventTime(time: string | null | undefined): string {
  if (!time) return '';
  const [hh, mm] = time.split(':').map(Number);
  if (!Number.isFinite(hh)) return time;
  const dt = new Date(2000, 0, 1, hh, mm || 0);
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(dt);
}

/**
 * Format an absolute UTC ISO instant (e.g. a stored remind_at) as its wall clock
 * IN the org timezone. An 8:00 org-local reminder reads as '… 8:00 AM', never the
 * UTC digits.
 */
export function formatAbsInTz(iso: string | null | undefined, tz: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: isValidTimeZone(tz) ? tz : DEFAULT_ORG_TZ,
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(d);
}

// ─── Universal date rendering (date-consistency) ────────────────────────────
// A single pair of formatters every surface renders dates through, so a value is
// NEVER shown as a raw UTC ISO ('2026-09-03T00:00:00.000Z') again. Date-only
// values render as their calendar date with NO zone shift; real timestamps render
// in the org zone with a wall-clock time.

/** '2026-09-03' — a bare SQL date (no time component). */
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
/** '2026-09-03T00:00:00.000Z' / '…T00:00:00Z' — a DATE column serialized at UTC midnight. */
const ISO_MIDNIGHT_UTC_RE = /^(\d{4})-(\d{2})-(\d{2})T00:00:00(?:\.000)?Z$/;
/** Any ISO-8601 date-time (with a real time-of-day and/or offset). */
const ISO_DATETIME_RE = /^(\d{4})-(\d{2})-(\d{2})T\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/;

export interface FormatDateOptions {
  /** Override the zone (tests, explicit callers). Defaults to the active org zone. */
  tz?: string;
  /** Render only the calendar date even for a real timestamp (drops the time). */
  dateOnly?: boolean;
  /** Force a wall-clock time for a real timestamp (default: shown automatically). */
  withTime?: boolean;
  /** What to render for null/undefined/empty. Default '—'. */
  empty?: string;
}

/**
 * 'Sep 3, 2026' from literal calendar parts — ZONE-INVARIANT. A date-only value
 * (a bare 'YYYY-MM-DD' or a UTC-midnight DATE) HAS no instant, so it must render
 * its literal y/mo/d on every runtime. We pin both the Date and the formatter to
 * UTC: whatever parts go in come straight back out, with no runtime-zone or
 * org-zone re-projection that could roll the day (the off-by-one that showed
 * 'Sep 2' for '2026-09-03' on a UTC server rendering a Central-time org).
 * Deliberately takes NO tz — a calendar date is never zone-shifted.
 */
function formatCalendarParts(y: number, mo: number, d: number): string {
  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (Number.isNaN(dt.getTime())) return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    month: 'short', day: 'numeric', year: 'numeric',
  }).format(dt);
}

/** 'Sep 3, 2026, 8:00 AM' — a real instant rendered in the org zone. */
function formatInstant(d: Date, tz: string, withTime: boolean): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    month: 'short', day: 'numeric', year: 'numeric',
    ...(withTime ? { hour: 'numeric', minute: '2-digit' } : {}),
  }).format(d);
}

/**
 * The one date formatter for the whole app. Given a value from the API (a bare
 * 'YYYY-MM-DD' date, a UTC-midnight ISO DATE, a real ISO timestamp, a Date, or an
 * epoch number) it returns a human string in the ORG timezone:
 *   - null / undefined / '' → '—' (or opts.empty)
 *   - a DATE-only value      → 'Sep 3, 2026'  (no zone shift, ever)
 *   - a real TIMESTAMP       → 'Sep 3, 2026, 8:00 AM'  (in the org zone)
 * It NEVER emits a raw ISO string. A value it cannot parse as a date is returned
 * as its own string (so a non-date that slipped through is shown verbatim, not
 * mangled).
 * @example formatDate('2026-09-03') → 'Sep 3, 2026'
 * @example formatDate('2026-09-03T00:00:00.000Z') → 'Sep 3, 2026'
 * @example formatDate('2026-09-03T13:00:00.000Z') → 'Sep 3, 2026, 8:00 AM' (America/Chicago)
 */
export function formatDate(value: unknown, opts: FormatDateOptions = {}): string {
  const empty = opts.empty ?? '—';
  if (value === null || value === undefined || value === '') return empty;
  const tz = resolveFormatTz(opts.tz);

  // Real Date / epoch instant → timestamp in org zone.
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return empty;
    return opts.dateOnly ? formatInstant(value, tz, false) : formatInstant(value, tz, opts.withTime ?? true);
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return formatInstant(new Date(value), tz, opts.dateOnly ? false : (opts.withTime ?? true));
  }

  const s = String(value).trim();
  if (!s) return empty;

  // Date-only shapes → render the literal calendar date, never touching any zone.
  // (opts.dateOnly is irrelevant here: a pure date is already date-only, and
  // projecting it into a zone would roll the day — the off-by-one this guards.)
  let m = DATE_ONLY_RE.exec(s) ?? ISO_MIDNIGHT_UTC_RE.exec(s);
  if (m) return formatCalendarParts(Number(m[1]), Number(m[2]), Number(m[3]));

  // A real ISO date-time → instant in org zone.
  if (ISO_DATETIME_RE.test(s)) {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    if (opts.dateOnly) return formatInstant(d, tz, false);
    return formatInstant(d, tz, opts.withTime ?? true);
  }

  // Last resort: something Date can parse (rare) — otherwise show the raw string.
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return formatInstant(d, tz, opts.dateOnly ? false : (opts.withTime ?? true));
  return s;
}

/** Keyword tokens whose presence in a column name marks it as a date/time field. */
const DATE_NAME_KEYWORDS = [
  'due', 'received', 'paid', 'approved', 'issued', 'effective',
  'board_order', 'sent', 'generated', 'connected',
];

/**
 * Does this column NAME denote a date/timestamp? Used by schema-driven renderers
 * (GenericDetail, generic list columns) to route a field through formatDate even
 * when the value arrives as a bare ISO string with no type metadata.
 * Matches: a `_date` / `_at` suffix, a `last_*` prefix, or one of the domain
 * date keywords (due/received/paid/approved/issued/effective/board_order/
 * sent/generated/connected) as a token.
 */
export function isDateFieldName(name: string): boolean {
  const n = String(name).toLowerCase();
  if (/_(date|at)$/.test(n)) return true;
  if (/^last_/.test(n)) return true;
  return DATE_NAME_KEYWORDS.some(
    (k) => n === k || n.startsWith(k + '_') || n.endsWith('_' + k) || n.includes('_' + k + '_'),
  );
}

/**
 * Does this column NAME denote a YEAR (a bare 4-digit calendar year, not a
 * count)? Year values must render as `2026`, never `2,026` — the thousands
 * separator a generic number formatter applies (§5.2). Matches a `year` column
 * or any `_year` suffix (tax_year, payable_year, reappraisal_year, process_year,
 * last_appraised_year, …). A plural `_years` duration (reappraisal_period_years)
 * is deliberately NOT matched — it is a count, formatted like any number.
 */
export function isYearFieldName(name: string): boolean {
  const n = String(name).toLowerCase();
  return n === 'year' || /_year$/.test(n);
}

/** Is `v` a string in an ISO date or date-time shape formatDate can render? */
export function isIsoDateString(v: unknown): boolean {
  if (typeof v !== 'string') return false;
  const s = v.trim();
  return DATE_ONLY_RE.test(s) || ISO_DATETIME_RE.test(s);
}

/**
 * Like formatDate but always renders the wall-clock time for a real instant —
 * for surfaces where the time genuinely matters (created/updated, hearing times,
 * sync/generation timestamps). A date-only value still renders as just its date
 * (there is no meaningful time to show).
 * @example formatDateTime('2026-09-03T13:00:00.000Z') → 'Sep 3, 2026, 8:00 AM'
 */
export function formatDateTime(value: unknown, opts: FormatDateOptions = {}): string {
  return formatDate(value, { ...opts, withTime: true });
}

/**
 * The UTC offset (minutes, east-positive) that `tz` was at for absolute `date`.
 * The standard Intl trick: format the instant AS `tz`, read that wall clock back
 * as UTC, and diff. DST-correct because it asks the zone at that specific instant.
 */
function offsetMinutes(tz: string, date: Date): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const map: Record<string, string> = {};
  for (const p of dtf.formatToParts(date)) map[p.type] = p.value;
  const asUTC = Date.UTC(
    Number(map.year), Number(map.month) - 1, Number(map.day),
    Number(map.hour), Number(map.minute), Number(map.second),
  );
  return Math.round((asUTC - date.getTime()) / 60000);
}

/**
 * Convert an org-local wall clock (a 'YYYY-MM-DDTHH:MM' value, as a
 * <input type="datetime-local"> yields, interpreted IN `tz`) to the absolute UTC
 * ISO instant — how a reminder's fire time is stored so 8:00 org-local fires at
 * 8:00 org-local regardless of the setter's browser zone. DST-safe (offset taken
 * at the target instant, corrected once across a boundary).
 */
export function zonedInputToUtcIso(localValue: string, tz: string): string | null {
  if (!localValue) return null;
  const zone = isValidTimeZone(tz) ? tz : DEFAULT_ORG_TZ;
  const [datePart, timePart] = localValue.split('T');
  if (!datePart) return null;
  const [y, mo, d] = datePart.split('-').map(Number);
  const [hh, mm] = (timePart || '00:00').split(':').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
  const naive = Date.UTC(y, mo - 1, d, hh || 0, mm || 0, 0);
  const off = offsetMinutes(zone, new Date(naive));
  let utc = naive - off * 60000;
  const off2 = offsetMinutes(zone, new Date(utc));
  if (off2 !== off) utc = naive - off2 * 60000;
  return new Date(utc).toISOString();
}

/**
 * Inverse of zonedInputToUtcIso: an absolute UTC ISO instant → the org-local
 * 'YYYY-MM-DDTHH:MM' string a <input type="datetime-local"> binds to, so editing
 * a stored reminder shows its org-local wall clock (not the UTC digits).
 */
export function utcIsoToZonedInput(iso: string | null | undefined, tz: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const zone = isValidTimeZone(tz) ? tz : DEFAULT_ORG_TZ;
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: zone, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  });
  const map: Record<string, string> = {};
  for (const p of dtf.formatToParts(d)) map[p.type] = p.value;
  return `${map.year}-${map.month}-${map.day}T${map.hour}:${map.minute}`;
}

/**
 * A curated IANA-zone list for the Org Settings picker — the US zones an appeal
 * practice works in, plus UTC. Any value the backend already holds is merged in
 * by the caller so a preset outside this list still shows.
 */
export const COMMON_TIMEZONES: Array<{ value: string; label: string }> = [
  { value: 'America/New_York', label: 'Eastern (America/New_York)' },
  { value: 'America/Chicago', label: 'Central (America/Chicago)' },
  { value: 'America/Denver', label: 'Mountain (America/Denver)' },
  { value: 'America/Phoenix', label: 'Mountain — no DST (America/Phoenix)' },
  { value: 'America/Los_Angeles', label: 'Pacific (America/Los_Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska (America/Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (Pacific/Honolulu)' },
  { value: 'UTC', label: 'UTC' },
];
